import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  SUPPORTED_LOCALES,
  currentLocalePrefix,
  localeUrl,
  splitLocalePath,
  type SupportedLocale,
} from "@/lib/locale";

interface SEOProps {
  title: string;
  description: string;
  path?: string;
  ogType?: string;
  noindex?: boolean;
  jsonLd?: any;
}

export const SEO = ({ title, description, path, ogType = "website", noindex = false, jsonLd }: SEOProps) => {
  const location = useLocation();
  const { i18n } = useTranslation();

  useEffect(() => {
    document.title = title;
    
    // Update meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', description);

    // Handle dynamic JSON-LD injection safely
    const oldScript = document.getElementById("json-ld-schema");
    if (oldScript) oldScript.remove();

    if (jsonLd) {
      const script = document.createElement("script");
      script.id = "json-ld-schema";
      script.type = "application/ld+json";
      script.innerHTML = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
    // Route path without any locale prefix — the router basename already
    // strips it from location.pathname, but `path` props may include it.
    const rawPath = path ?? location.pathname;
    const routePath = splitLocalePath(rawPath).path;
    const activeLocale = currentLocalePrefix();
    // Canonical always points at a locale-prefixed URL; the unprefixed URL is
    // reserved for x-default (auto-detected language).
    // Fall back to the active i18n language when the URL carries no prefix
    // (e.g. the auto-detected root URL) so canonicals stay per-locale.
    const i18nLocale = (i18n.language || "en").split("-")[0] as SupportedLocale;
    const canonicalLocale: SupportedLocale =
      activeLocale ?? (SUPPORTED_LOCALES.includes(i18nLocale) ? i18nLocale : "en");
    const url = localeUrl(canonicalLocale, routePath);

    const setMeta = (attr: "name" | "property", key: string, value: string) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };

    // Self-referential canonical
    let canonical = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);

    // Reciprocal hreflang set: one per locale + x-default on the unprefixed URL.
    document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((n) => n.remove());
    if (!noindex) {
      const alternates: [string, string][] = [
        ...SUPPORTED_LOCALES.map((l) => [l, localeUrl(l, routePath)] as [string, string]),
        ["x-default", localeUrl(null, routePath)],
      ];
      for (const [lang, href] of alternates) {
        const link = document.createElement("link");
        link.setAttribute("rel", "alternate");
        link.setAttribute("hreflang", lang);
        link.setAttribute("href", href);
        document.head.appendChild(link);
      }
    }

    setMeta("property", "og:type", ogType);
    setMeta("property", "og:url", url);
    setMeta("property", "og:locale", canonicalLocale);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:site_name", "Valorform");
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);



    let robots = document.querySelector('meta[name="robots"]');
    if (noindex) {
      if (!robots) {
        robots = document.createElement('meta');
        robots.setAttribute('name', 'robots');
        document.head.appendChild(robots);
      }
      robots.setAttribute('content', 'noindex, nofollow');
    } else if (robots) {
      robots.remove();
    }
  }, [title, description, jsonLd, ogType, noindex, path, location.pathname, i18n.language]);

  return null;
};

export default SEO;
